'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { Toggle as TogglePrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

/** Upstream shadcn toggle shell (layout, focus ring, disabled, icon sizing). */
export const upstreamToggleVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium outline-none transition-colors focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4',
);

/** Genesys editor colour and density variants layered on {@link upstreamToggleVariants}. */
export const genesysToggleVariants = cva(
  'hover:bg-muted hover:text-muted-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground',
  {
    variants: {
      variant: {
        light:
          'bg-input-idle text-secondary-foreground hover:bg-input-idle data-[state=on]:bg-input-idle data-[state=on]:text-primary-foreground',
        transparent:
          'bg-transparent text-secondary-foreground hover:bg-input-idle active:bg-input-hover active:text-primary-foreground',
        default: 'bg-transparent',
        outline:
          'border-2 border-transparent bg-input-idle text-tertiary hover:border-tertiary hover:bg-input-idle hover:text-foreground data-[state=on]:border-primary data-[state=on]:bg-input-idle data-[state=on]:border-transparent data-[state=on]:text-secondary-foreground',
        popover:
          'bg-popover text-popover-foreground shadow-xs hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground',
        overlay:
          'border border-none bg-input-idle shadow-xs text-accent-foreground hover:bg-input-idle hover:text-primary-foreground data-[state=on]:bg-input-idle data-[state=on]:text-primary-foreground',
        titleBar:
          'bg-input-idle text-foreground hover:bg-input-hover hover:text-chart-1 active:bg-primary active:text-primary-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
      },
      size: {
        xs: 'h-6 min-w-6 px-1.5',
        light: 'h-7 px-3 text-sm',
        default: 'h-7 min-w-9 px-3',
        sm: 'h-8 min-w-8 px-1.5',
        lg: 'h-10 min-w-10 px-2.5',
        titleBar: 'h-[28px] min-w-[28px] px-[7px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ToggleProps = React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof genesysToggleVariants>;

export const Toggle = React.forwardRef<React.ComponentRef<typeof TogglePrimitive.Root>, ToggleProps>(
  ({ className, variant, size, ...props }, ref) => (
    <TogglePrimitive.Root
      ref={ref}
      data-slot="toggle"
      className={cn(upstreamToggleVariants(), genesysToggleVariants({ variant, size }), className)}
      {...props}
    />
  ),
);

Toggle.displayName = TogglePrimitive.Root.displayName ?? 'Toggle';
